import React from 'react';
import { MainLayout } from 'layouts/MainLayout';
import { Box } from '@mui/material';
import { Permission, Resource } from '@pepperhq/auth-client';

import { TrackingLinksTable } from '../../../components/tracking-links/components/TrackingLinksTable';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getAllLocationTitles } from 'store/locations/locationsSlice';
import { UnlockPremiumUTMBanner } from 'components/tracking-links/components/UnlockPremiumUTMBanner';
import { linksApi } from 'components/tracking-links/trackingLinksApi';
import { TrackingLinkType } from 'components/tracking-links/trackingLinks';
import { DEFAULT_PAGE_LIMIT } from 'lib/hooks/usePagedData';
import logger from 'lib/logger';

const APP_LINK_LIMIT = 1;

// Campaign Links === Tracking Links
export const TrackingLinksPage = () => {
    const { settings } = useAppSelector(state => state.settings);
    const { locations } = useAppSelector(state => state.locations);
    const { claims } = useAppSelector(state => state.auth);
    const dispatch = useAppDispatch();

    // TODO: Add to roles table
    const hasLinksWritePermission = React.useMemo(
        () => claims.hasPermission(Resource.Links, Permission.write),
        [claims]
    );

    React.useEffect(() => {
        getAllLocationTitles(dispatch);
    }, [dispatch]);

    const unlimitedAppLinksEnabled = settings?.links?.unlimitedAppLinksEnabled ?? false;
    const utmParametersEnabled = settings?.links?.utmParametersEnabled ?? false;

    const showUnlockPremiumUTMBanner = React.useMemo(
        () => !unlimitedAppLinksEnabled || !utmParametersEnabled,
        [unlimitedAppLinksEnabled, utmParametersEnabled]
    );

    // null until count is fetched
    const [appLinkCount, setAppLinkCount] = React.useState<number | null>(null);

    React.useEffect(() => {
        const fetchAppLinkCount = async () => {
            try {
                const { body, ok } = await linksApi.getList({
                    queryParameters: [
                        { key: 'limit', value: DEFAULT_PAGE_LIMIT },
                        { key: 'type', value: TrackingLinkType.APP_LINK }
                    ]
                });
                if (ok && body.items) {
                    const count = body.items.length;
                    setAppLinkCount(count);
                }
            } catch (error) {
                logger.error(error);
            }
        };
        if (!unlimitedAppLinksEnabled) {
            fetchAppLinkCount();
        }
    }, [unlimitedAppLinksEnabled]);

    const appLinksLimitReached =
        !unlimitedAppLinksEnabled && (appLinkCount === null || appLinkCount >= APP_LINK_LIMIT);

    const handleAppLinkCreated = React.useCallback(() => {
        setAppLinkCount(currentCount => (currentCount === null ? 1 : currentCount + 1));
    }, []);

    return (
        <MainLayout
            pageName="Campaign Links"
            pageDescription="Create trackable links to measure campaign performance."
            noScroll
        >
            <Box display="flex" flex={1} flexDirection="column" gap={2} minHeight={0}>
                {showUnlockPremiumUTMBanner && <UnlockPremiumUTMBanner />}
                <TrackingLinksTable
                    hasLinksWritePermission={hasLinksWritePermission}
                    locations={locations}
                    utmParametersEnabled={utmParametersEnabled}
                    appLinksLimitReached={appLinksLimitReached}
                    onAppLinkCreated={handleAppLinkCreated}
                />
            </Box>
        </MainLayout>
    );
};
